// src/pages/earningsImport/RideHailingWeekPeriods.js
import React, { useEffect, useState } from "react";
import { useNotify } from "react-admin";
import { auth } from "../../firebase/firebase";
import {
  RIDE_HAILING_WEEK_KEYS,
  loadRideHailingWeekPeriods,
  makeDefaultRideHailingWeeks,
  saveRideHailingWeekPeriods,
} from "./rideHailingWeekPeriodUtils";

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function RideHailingWeekPeriods() {
  const notify = useNotify();
  const [month, setMonth] = useState(currentMonthValue());
  const [weeks, setWeeks] = useState(makeDefaultRideHailingWeeks(currentMonthValue()));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!month) return;
    setLoading(true);
    try {
      const data = await loadRideHailingWeekPeriods(month);
      setWeeks(data.weeks || makeDefaultRideHailingWeeks(month));
    } catch (err) {
      console.error(err);
      notify("Failed to load ride-hailing week periods", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const updateWeek = (weekKey, field, value) => {
    setWeeks((prev) => ({
      ...prev,
      [weekKey]: {
        ...(prev[weekKey] || {}),
        [field]: value,
      },
    }));
  };

  const resetToDefault = () => {
    setWeeks(makeDefaultRideHailingWeeks(month));
  };

  const validate = () => {
    for (const key of RIDE_HAILING_WEEK_KEYS) {
      const row = weeks[key] || {};
      if (!row.startDate || !row.endDate) return `${key}: start and end date are required.`;
      const s = new Date(`${row.startDate}T00:00:00`);
      const e = new Date(`${row.endDate}T00:00:00`);
      if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${key}: invalid date.`;
      if (e < s) return `${key}: end date cannot be before start date.`;
    }
    return "";
  };

  const save = async () => {
    const error = validate();
    if (error) {
      notify(error, { type: "warning" });
      return;
    }

    setSaving(true);
    try {
      await saveRideHailingWeekPeriods(month, weeks, auth.currentUser?.email || "");
      notify("Ride-hailing week periods saved", { type: "success" });
    } catch (err) {
      console.error(err);
      notify("Failed to save ride-hailing week periods", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 950 }}>
      <h2 style={{ marginTop: 0 }}>Ride-Hailing Week Periods</h2>
      <p style={{ color: "#555", lineHeight: 1.5 }}>
        Configure the actual platform week dates used for weekly ride-hailing reports. These dates are used only for
        matching income to driver/car assignment periods. The income will still appear in the selected statement month
        and selected week column.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center", margin: "18px 0" }}>
        <label style={{ fontWeight: 700 }}>Statement month</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ padding: 8, border: "1px solid #ccc", borderRadius: 4 }}
        />
        <button onClick={load} disabled={loading} style={button("#6c757d")}>
          {loading ? "Loading..." : "Reload"}
        </button>
        <button onClick={resetToDefault} style={button("#f59e0b")}>
          Reset calendar weeks
        </button>
        <button onClick={save} disabled={saving} style={button("#198754")}>
          {saving ? "Saving..." : "Save periods"}
        </button>
      </div>

      <table style={{ borderCollapse: "collapse", width: "100%", background: "white" }}>
        <thead>
          <tr style={{ background: "#f1f5f9" }}>
            <th style={th}>Week</th>
            <th style={th}>Start date</th>
            <th style={th}>End date</th>
            <th style={th}>Example use</th>
          </tr>
        </thead>
        <tbody>
          {RIDE_HAILING_WEEK_KEYS.map((key) => (
            <tr key={key}>
              <td style={td}>{key.toUpperCase()}</td>
              <td style={td}>
                <input
                  type="date"
                  value={weeks[key]?.startDate || ""}
                  onChange={(e) => updateWeek(key, "startDate", e.target.value)}
                  style={input}
                />
              </td>
              <td style={td}>
                <input
                  type="date"
                  value={weeks[key]?.endDate || ""}
                  onChange={(e) => updateWeek(key, "endDate", e.target.value)}
                  style={input}
                />
              </td>
              <td style={tdSmall}>
                Uploading {key} income for {month} will use this date range only to find the correct assigned car.
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 18, padding: 14, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6 }}>
        <strong>Important:</strong> If August week 3 report is 10-Aug to 17-Aug, enter that here. The income still stays in
        August week3, but assignment matching will check the real 10-Aug to 17-Aug period instead of assuming 15-Aug.
      </div>
    </div>
  );
}

const th = { padding: 10, border: "1px solid #ddd", textAlign: "left" };
const td = { padding: 10, border: "1px solid #ddd" };
const tdSmall = { ...td, color: "#555", fontSize: 13 };
const input = { padding: 7, border: "1px solid #ccc", borderRadius: 4, width: 150 };
const button = (bg) => ({ padding: "8px 12px", border: "none", borderRadius: 4, background: bg, color: "white", cursor: "pointer" });
